// API fetch 래퍼 (모바일 셸 → Vercel 백엔드)
// - X-Client-Id 헤더 자동 부착 (8.6 절충안 — middleware 분기는 추후 별도 세션)
// - X-Client-Source: 'sotda-mobile' 고정 → 서버 로그에서 모바일 트래픽 구분
// - Supabase 세션 토큰을 Authorization 헤더에 자동 부착 (있을 때만)
// - credentials: 'include' → OAuth 쿠키 흐름 유지

import { Preferences } from "@capacitor/preferences";
import { getAccessTokenSafe } from "./authSession";
import { E2E_MOCK, e2eResolve } from "./e2eMock";
import { recordNetworkSuccess, recordTransportFailure } from "./netHealth";
import { clearRateLimited, recordRateLimited } from "./rateLimitState";

const API_BASE = (import.meta.env.VITE_API_BASE as string) || "https://sotda.com";
const CLIENT_ID_KEY = "sotda-client-id";

// device 단위 UUID 1회 발급 → 영구 보관
// (앱 삭제 시 리셋, NAT 공유 IP 안에서도 디바이스 구분용)
let cachedClientId: string | null = null;
async function getClientId(): Promise<string> {
  if (cachedClientId) return cachedClientId;
  const { value } = await Preferences.get({ key: CLIENT_ID_KEY });
  if (value) {
    cachedClientId = value;
    return value;
  }
  // crypto.randomUUID는 secure context(https/Capacitor WebView)에서 동작
  const newId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : // fallback: 옛 환경용 (실제로는 도달 가능성 거의 0)
        `sotda-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  await Preferences.set({ key: CLIENT_ID_KEY, value: newId });
  cachedClientId = newId;
  return newId;
}

export class ApiError extends Error {
  status: number;
  body: string;
  url: string;
  constructor(status: number, body: string, url: string) {
    super(`API ${status} ${url}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.url = url;
  }
}

export type ApiFetchInit = Omit<RequestInit, "body"> & {
  body?: unknown; // 객체면 JSON.stringify 자동, string/FormData는 그대로 전달
  // signal, headers 등은 RequestInit 그대로
};

/**
 * 통합 API fetch.
 * @param path '/api/...' 상대경로 또는 절대 URL
 * @param init RequestInit 확장 (body 객체 자동 직렬화)
 */
export async function apiFetch<T = unknown>(path: string, init: ApiFetchInit = {}): Promise<T> {
  // E2E mock — VITE_E2E_MOCK 빌드에서 시나리오 데이터를 네트워크 없이 반환 (prod엔 tree-shake)
  if (E2E_MOCK) {
    const mock = e2eResolve(path);
    if (mock !== undefined) return mock as T;
  }
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const clientId = await getClientId();

  const headers = new Headers(init.headers as HeadersInit | undefined);
  headers.set("X-Client-Id", clientId);
  headers.set("X-Client-Source", "sotda-mobile");

  // Supabase 세션 토큰 자동 첨부 (있을 때만 — 익명 API도 그대로 호출 가능)
  // ⚠ 2026-07-25: 여기서 `supabase.auth.getSession()`을 직접 await 하면 오프라인에서
  //   auth 락 정체로 **무한 대기**해 화면이 로딩에 고착됐다(경로 상세는 [[authSession.ts]]).
  //   getAccessTokenSafe는 캐시 우선 + 타임아웃이라 어떤 경우에도 유한 시간에 돌아온다.
  //   degraded(세션 확인 실패)여도 호출은 계속한다 — 진짜 실패는 아래 fetch가 판정하고,
  //   그 실패가 recordTransportFailure로 이어져 네트워크 힌트가 정상 동작한다.
  const { token } = await getAccessTokenSafe();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // body 직렬화 처리
  let body: BodyInit | undefined;
  if (init.body !== undefined && init.body !== null) {
    if (typeof init.body === "string" || init.body instanceof FormData || init.body instanceof Blob) {
      body = init.body as BodyInit;
    } else {
      body = JSON.stringify(init.body);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    }
  }

  // 멱등 GET/HEAD만 자동 재시도 — Yahoo 시세 간헐 429/5xx·순간 네트워크 실패를 흡수
  //   (POST/PUT/DELETE는 중복 주문 등 위험이라 1회만 시도). 화면단 자동 재시도 부재를 여기서 근본 보완.
  const method = (init.method ?? "GET").toUpperCase();
  const idempotent = method === "GET" || method === "HEAD";
  const maxAttempts = idempotent ? 3 : 1;
  const RETRY_DELAY_MS = [350, 900]; // 시도 간 백오프(ms)
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  let res: Response;
  for (let attempt = 0; ; attempt += 1) {
    // 호출자 signal 우선, 없으면 시도마다 15s 타임아웃 부여(타임아웃 부재로 인한 무한 대기 차단)
    const signal =
      init.signal ??
      (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal ? AbortSignal.timeout(15000) : undefined);
    try {
      res = await fetch(url, { ...init, headers, body, credentials: "include", signal });
    } catch (e) {
      // 네트워크/타임아웃 실패 — 멱등이고 재시도 여유 있으면 백오프 후 재시도
      if (idempotent && attempt < maxAttempts - 1) {
        await sleep(RETRY_DELAY_MS[attempt]);
        continue;
      }
      // 재시도까지 모두 실패 = 전송계층 실패 1건으로 기록(VPN·DNS 앱 후킹 감지용).
      // 호출자가 직접 abort한 경우(화면 이탈 등)는 네트워크 문제가 아니므로 제외.
      if (init.signal?.aborted !== true) recordTransportFailure(path);
      throw e;
    }
    // 응답을 받았다 = 전송 경로 정상(4xx/5xx여도 서버가 답한 것) → 누적 실패 해제
    recordNetworkSuccess();
    // 🔴 429는 재시도하지 않는다 (2026-07-26 사고) — 서버가 "잠시 멈추라"고 답한 것이라
    //   350ms 뒤 다시 두드리면 한도를 더 태워 스스로 빠져나오지 못하는 악순환이 된다.
    //   실측(prod 19:18): 재시도 증폭으로 68건이 전부 429. Retry-After(60s)도 무시되고 있었다.
    //   대신 전역 신호로 남겨 화면이 "네트워크 확인" 대신 사실대로 안내하게 한다.
    // N-25: 어느 버킷이 막혔는지는 서버가 알려준다(X-RateLimit-Bucket) — 클라가 경로로
    //   추측하면 규칙이 두 곳에 복제된다. 헤더가 없으면 unknown으로 다룬다.
    const rlBucket = res.headers.get("X-RateLimit-Bucket");
    if (res.status === 429) {
      recordRateLimited(Number(res.headers.get("Retry-After")) || null, rlBucket);
      break;
    }
    // 🔑 N-25: **그 버킷만** 해제한다. 이전엔 버킷 구분 없이 전역 상태를 0으로 만들어,
    //   쓰기 429 직후 병렬 시세 읽기 200 하나가 아직 유효한 안내를 즉시 지웠다.
    clearRateLimited(rlBucket);
    // 5xx = 일시 실패로 보고 재시도(멱등 한정). 4xx 등 영구 실패는 재시도하지 않음
    if (!res.ok && idempotent && attempt < maxAttempts - 1 && res.status >= 500) {
      await sleep(RETRY_DELAY_MS[attempt]);
      continue;
    }
    break;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text, url);
  }

  // 204 No Content 등 빈 응답 안전 처리
  const ct = res.headers.get("Content-Type") ?? "";
  if (res.status === 204 || !ct.includes("application/json")) {
    return undefined as unknown as T;
  }
  return (await res.json()) as T;
}

// VITE_USE_MOCK 플래그 — 8.2 페이지에서 mock fallback 분기에 사용
export const USE_MOCK = (import.meta.env.VITE_USE_MOCK as string) === "true";
